<template>
  <div>
    <base-header class="pb-6"> </base-header>
    <div class="container-fluid mt--6">
      <div class="card">
        <div class="card-header border-0">
          <div class="row">
            <div class="col-12 col-lg-4">
              <h3 class="mt-4 mb-4 title-2 bold">Espera del repartidor</h3>
            </div>
            <div class="col-12 col-lg-8">
              <div class="d-flex flex-wrap justify-content-end espera-repartidor__links">
                <h3 class="mt-4 mb-4 link-discount" @click.prevent="showExplanation = true">
                  ¿Cómo se calcula la espera del repartidor?
                </h3>
                <h3 class="mt-4 mb-4 link-discount" @click.prevent="showDiscount = true">
                  Ver cuadro de descuentos
                </h3>
              </div>
            </div>
          </div>
        </div>
        <div class="row starter-page">
          <div class="container-fluid">
            <div class="col-12 mb-4">
              <base-input addon-left-icon="ni ni-calendar-grid-58">
                <flat-picker
                  slot-scope="{ focus, blur }"
                  @on-open="focus"
                  @on-close="blur"
                  :config="dates.config"
                  class="form-control datepicker"
                  placeholder="Seleccione fecha"
                  v-model="dates.range"
                  @input="dateRangeChange"
                >
                </flat-picker>
              </base-input>
            </div>
            <p v-if="isLoadingDealerWait" class="col-12 text-muted">{{ LOADING_TEXT }}</p>
            <p v-else-if="hasDealerWaitError" class="col-12 text-danger">{{ LOAD_ERROR_TEXT }}</p>
            <DealerWaitRoutesSection
              v-if="showsDealerWait"
              :detail="dealerWait"
              :charge-tiers="chargeTiers"
            />
            <LegacyLatenessSection
              v-if="showsLegacyLateness"
              :init-date="initDate"
              :end-date="endDate"
              :change-date="dealerWait.formulaChangeDate"
            />
          </div>
        </div>
      </div>
    </div>
    <CustomModal :is-modal="showDiscount" @closeModal="showDiscount = false" :closeOuth="true">
      <h5 class="modal-title text semi-bold">Cuadro de descuentos:</h5>
      <div class="mt-4 mb-4 p-4">
        <el-table :data="chargeTiers" row-key="label" header-row-class-name="thead-desing-dark" class="desing-dark">
          <el-table-column label="Exceso de espera" prop="label" min-width="200px" />
          <el-table-column label="Monto" prop="amount" min-width="116px">
            <template v-slot="{ row }"><strong>{{ formatMoney(row.currency, row.amount) }}</strong></template>
          </el-table-column>
        </el-table>
      </div>
    </CustomModal>
    <CustomModal :is-modal="showExplanation" @closeModal="showExplanation = false" :closeOuth="true" width="xlg">
      <DealerWaitExplanation :charge-tiers="chargeTiers" :change-date="dealerWait && dealerWait.formulaChangeDate" />
    </CustomModal>
  </div>
</template>

<script>
import { Table, TableColumn } from "element-ui";
import { mapActions, mapState } from "vuex";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { Spanish } from "flatpickr/dist/l10n/es";
import CustomModal from "@/components/CustomModal";
import DealerWaitRoutesSection from "@/components/EsperaRepartidor/DealerWaitRoutesSection";
import LegacyLatenessSection from "@/components/EsperaRepartidor/LegacyLatenessSection";
import DealerWaitExplanation from "@/components/EsperaRepartidor/DealerWaitExplanation";
import { formatMoney } from "@/utils/dailyMenuLevelHelpTexts";

const DEFAULT_RANGE_DAYS = 14;
const RANGE_SEPARATOR = " a ";
const RANGE_PARTS = 2;
const DATE_PART_LENGTH = 2;
const DATE_PART_PADDING = "0";
const FIRST_MONTH_OFFSET = 1;
const LOADING_TEXT = "Cargando la espera del repartidor…";
const LOAD_ERROR_TEXT = "No pudimos cargar la espera del repartidor. Vuelve a elegir las fechas para intentarlo de nuevo.";

const padDatePart = (value) => String(value).padStart(DATE_PART_LENGTH, DATE_PART_PADDING);

const toLocalDay = (date) =>
  `${date.getFullYear()}-${padDatePart(date.getMonth() + FIRST_MONTH_OFFSET)}-${padDatePart(date.getDate())}`;

const defaultRange = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (DEFAULT_RANGE_DAYS - 1));
  return [toLocalDay(start), toLocalDay(today)];
};

export default {
  name: "ListEsperaRepartidor",
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    flatPicker,
    CustomModal,
    DealerWaitRoutesSection,
    LegacyLatenessSection,
    DealerWaitExplanation
  },
  data() {
    const [initDate, endDate] = defaultRange();
    return {
      LOADING_TEXT,
      LOAD_ERROR_TEXT,
      showDiscount: false,
      showExplanation: false,
      initDate,
      endDate,
      dates: {
        range: `${initDate}${RANGE_SEPARATOR}${endDate}`,
        config: { allowInput: false, mode: "range", locale: Spanish }
      }
    };
  },
  computed: {
    ...mapState("esperaRepartidor", ["dealerWait", "chargeTiers", "isLoadingDealerWait", "hasDealerWaitError"]),
    showsDealerWait() {
      return this.dealerWait !== null && this.endDate >= this.dealerWait.formulaChangeDate;
    },
    showsLegacyLateness() {
      return this.dealerWait !== null && this.initDate < this.dealerWait.formulaChangeDate;
    }
  },
  mounted() {
    this.GET_CHARGE_TIERS();
    this.loadRange();
  },
  methods: {
    formatMoney,
    ...mapActions("esperaRepartidor", ["GET_DEALER_WAIT", "GET_CHARGE_TIERS"]),
    dateRangeChange(value) {
      const days = value.split(RANGE_SEPARATOR.trim()).map((day) => day.trim());
      if (days.length === RANGE_PARTS) {
        [this.initDate, this.endDate] = days;
        this.loadRange();
      }
    },
    loadRange() {
      this.GET_DEALER_WAIT({ startDate: this.initDate, endDate: this.endDate });
    }
  }
};
</script>

<style scoped>
.espera-repartidor__links {
  column-gap: 2rem;
}
</style>
